/**
 * User Management Dashboard
 *
 * Comprehensive user management system with tabs for:
 * - Users
 * - Roles
 * - Groups
 * - Departments
 * - Designations
 *
 * This provides a single entry point for all user-related configuration
 */

import React, { useState, useEffect } from 'react';
import { Tabs, Card, Row, Col } from 'antd';
import {
  UserOutlined,
  SafetyOutlined,
  TeamOutlined,
  CloudServerOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import UserConfig2 from './UserConfig2';
import RoleManagement from './RoleManagement';
import GroupManagement from './GroupManagement';
import ActiveDirectoryIntegration from './ActiveDirectoryIntegration';
import UserAccessManagement from './UserAccessManagement';
import StatCard from '@/components/StatCard';
import { Users, UserCheck, Shield, Award } from 'lucide-react';
import { get_employee_user_list, get_roles, get_departments } from '@/utils/api';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import PageHeader from '@/components/PageHeader';
import { HeaderActionsContext } from '@/components/HeaderActions';


const UserManagementDashboard = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('users');
  const [actionsSlot, setActionsSlot] = useState(null);   // where the Users tab puts Add User in this page's header
  const [employees_list, set_employees_list] = useState([]);
  const [roles_list, set_roles_list] = useState([]);
  const [departments_list, set_departments_list] = useState([]);

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await get_employee_user_list((res) => {
          set_employees_list(res?.data || []);
        });
        await get_roles((res) => {
          set_roles_list(res?.data?.data || []);
        });
        await get_departments((res) => {
          set_departments_list(res?.data?.data || []);
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        classNames={{ body: 'page-card-scroll' }}
        style={{ backgroundColor: cardBgColor, height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
        title={
          <PageHeader
            compact
            icon={<Icons.Users size={22} />}
            title={t('User Management')}
            description="Users, roles, groups, access and directory integration in one place."
            extra={<span ref={setActionsSlot} className="inline-flex items-center gap-2" />}
          />
        }
      >
        {/* Top Summary Cards - Above Tabs */}
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} md={6}>
            <StatCard
              label="Total Users"
              value={employees_list?.length || 0}
              icon={<Users size={24} />}
              bgColor={`${defaultColors.primary}22`}
              iconColor={defaultColors.primary}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatCard
              label="Active Users"
              value={employees_list?.filter((emp) => emp?.is_active)?.length || 0}
              icon={<UserCheck size={24} />}
              bgColor={`${defaultColors.success}22`}
              iconColor={defaultColors.success}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatCard
              label="Total Roles"
              value={roles_list?.length || 0}
              icon={<Shield size={24} />}
              bgColor={`${defaultColors.info}22`}
              iconColor={defaultColors.info}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatCard
              label="Departments"
              value={departments_list?.length || 0}
              icon={<Award size={24} />}
              bgColor={`${defaultColors.warning}22`}
              iconColor={defaultColors.warning}
            />
          </Col>
        </Row>

        <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="large"
            tabBarStyle={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9f9f9', borderRadius: '8px', padding: '2px 8px' }}
            items={[
              { key: 'users', label: <span><UserOutlined style={{ marginRight: 8 }} />Users</span>, children: (
                <HeaderActionsContext.Provider value={actionsSlot}>
                  <UserConfig2 isEmbedded active={activeTab === 'users'} />
                </HeaderActionsContext.Provider>
              ) },
              { key: 'roles', label: <span><SafetyOutlined style={{ marginRight: 8 }} />Roles & Permissions</span>, children: (
                <HeaderActionsContext.Provider value={actionsSlot}>
                  <RoleManagement active={activeTab === 'roles'} />
                </HeaderActionsContext.Provider>
              ) },
              { key: 'groups', label: <span><TeamOutlined style={{ marginRight: 8 }} />Groups</span>, children: (
                <HeaderActionsContext.Provider value={actionsSlot}>
                  <GroupManagement active={activeTab === 'groups'} />
                </HeaderActionsContext.Provider>
              ) },
              { key: 'ad', label: <span><CloudServerOutlined style={{ marginRight: 8 }} />Active Directory</span>, children: (
                <HeaderActionsContext.Provider value={actionsSlot}>
                  <ActiveDirectoryIntegration active={activeTab === 'ad'} />
                </HeaderActionsContext.Provider>
              ) },
              { key: 'access', label: <span><KeyOutlined style={{ marginRight: 8 }} />User Access Management</span>, children: <UserAccessManagement /> },
            ]}
        />
      </Card>
    </div>
  );
};

export default UserManagementDashboard;
